import { NavSection } from "../navigation/NavSection";
import { NAVIGATION_CONFIG } from "@/config/navigation";
import {
  buildFamilyHubSubmenu,
  FAMILY_HUB_NAV_BASE,
  withDevPreviewNavItems,
} from "@/config/familyHubSidebarNav";
import { isFamilyHubDevPreviewActive } from "@/utils/familyHubDevPreview";
import { useLocation } from "react-router-dom";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { useModeratorStatus } from "@/hooks/useModeratorStatus";
import { useInvitations } from "@/hooks/useInvitations";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";

interface SidebarNavigationProps {
  isCollapsed: boolean;
  setIsMenuOpen: (value: boolean) => void;
  isMobile?: boolean;
  toggleSidebar?: () => void;
}

/**
 * Sidebar Navigation.
 */
export function SidebarNavigation({
  isCollapsed,
  setIsMenuOpen,
  isMobile = false,
  toggleSidebar,
}: Readonly<SidebarNavigationProps>) {
  const { pathname, search } = useLocation();
  const { isAdmin } = useAdminStatus();
  const { data: isModerator } = useModeratorStatus();
  const { pendingInvitations } = useInvitations();
  const { isPaid, isLoading: subscriptionLoading } = useSubscriptionStatus();
  const pendingCount = pendingInvitations.length;

  // Determine if user can moderate: admin, super_admin, or moderator
  // Show moderation options if user is admin OR moderator (don't hide while loading)
  const canModerate = isAdmin || isModerator === true;

  // Filter blog submenu items based on moderator status
  const filteredMainItems = NAVIGATION_CONFIG.main.map((item) => {
    if (item.href === "/blog" && item.submenu) {
      return {
        ...item,
        submenu: item.submenu.filter((subItem) => {
          // Hide "Moderation" and "Comment Moderation" if user cannot moderate
          // Show if user is admin OR moderator (don't hide while loading)
          if (subItem.href === "/blog/moderation" || subItem.href === "/blog/moderation/comments") {
            return canModerate;
          }
          return true;
        }),
      };
    }
    return item;
  });

  const premiumItems = NAVIGATION_CONFIG.premium.map((item) => {
    if (item.href !== FAMILY_HUB_NAV_BASE) {
      return item;
    }
    const submenu = buildFamilyHubSubmenu(FAMILY_HUB_NAV_BASE);
    const usePreview =
      import.meta.env.DEV &&
      pathname === FAMILY_HUB_NAV_BASE &&
      isFamilyHubDevPreviewActive(search);
    return {
      ...item,
      href: usePreview ? `${FAMILY_HUB_NAV_BASE}?preview=1` : FAMILY_HUB_NAV_BASE,
      submenu: usePreview ? withDevPreviewNavItems(submenu) : submenu,
    };
  });

  return (
    <div className="flex-1 py-2">
      {/* Collapse toggle as navigation item (before Dashboard) */}
      {!isMobile && toggleSidebar && (
        <div className={cn("py-2", "px-4")}>
          <Button
            variant="ghost"
            onClick={toggleSidebar}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={cn(
              "w-full justify-start font-medium transition-all duration-200 rounded-lg group",
              isMobile ? "h-10 px-4 text-sm" : "h-9 px-3 text-sm",
              "text-foreground/70 hover:bg-muted/50 hover:text-foreground",
              isCollapsed && "px-2 justify-center"
            )}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <PanelLeftOpen className="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
            ) : (
              <PanelLeftClose className="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
            )}
          </Button>
        </div>
      )}

      <NavSection
        title=""
        items={filteredMainItems}
        setIsMenuOpen={setIsMenuOpen}
        currentPath={pathname}
        isCollapsed={isCollapsed}
        isMobile={isMobile}
        badgesByHref={{ "/family": pendingCount }}
        isPaid={isPaid}
        subscriptionLoading={subscriptionLoading}
      />

      <div className={cn("my-4 border-t border-border/50", isMobile ? "mx-6" : "mx-4")}></div>

      <NavSection
        title="Premium Features"
        items={premiumItems}
        setIsMenuOpen={setIsMenuOpen}
        currentPath={pathname}
        isCollapsed={isCollapsed}
        isMobile={isMobile}
        badgesByHref={{ "/family": pendingCount }}
        isPaid={isPaid}
        subscriptionLoading={subscriptionLoading}
      />

      {/* Admin section - show only for admin users */}
      {isAdmin && (
        <>
          <div className={cn("my-4 border-t border-border/50", isMobile ? "mx-6" : "mx-4")}></div>
          <NavSection
            title="Admin"
            items={NAVIGATION_CONFIG.adminRoutes}
            setIsMenuOpen={setIsMenuOpen}
            currentPath={pathname}
            isCollapsed={isCollapsed}
            isMobile={isMobile}
            isPaid={isPaid}
            subscriptionLoading={subscriptionLoading}
          />
        </>
      )}
    </div>
  );
}
